import React, { useState } from 'react';
import { Info, X, Database, Copy, Check } from 'lucide-react';
import { useAppAuth } from '../contexts/AuthContext';

/**
 * QueryInfoButton - A button that displays query information in a modal
 * Only visible to users with 'system:view-queries' permission (sysadmin).
 * @param {Object} props
 * @param {Array<{name: string, description: string, query: string, endpoint?: string}>} props.queries - Array of query info objects
 * @param {string} props.title - Optional title for the modal
 */
export default function QueryInfoButton({ queries, title = 'Data Sources' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const { hasPermission } = useAppAuth();

  const handleCopy = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!queries || queries.length === 0) return null;
  if (!hasPermission('system:view-queries')) return null;

  return (
    <>
      {/* Info Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 hover:border-amber-300 transition-colors"
        title="View data source queries"
      >
        <Info className="w-4 h-4" />
        <span>Query Info</span>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl transform transition-all">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Database className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <p className="text-sm text-gray-500">
                      {queries.length} {queries.length === 1 ? 'query' : 'queries'} used on this page
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-4">
                {queries.map((query, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Query Header */}
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{query.name}</h4>
                          {query.endpoint && (
                            <p className="text-xs text-gray-500 mt-0.5 font-mono">
                              API: {query.endpoint}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleCopy(query.query, index)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          {copiedIndex === index ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-green-500" />
                              <span className="text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              <span>Copy SQL</span>
                            </>
                          )}
                        </button>
                      </div>
                      {query.description && (
                        <p className="text-sm text-gray-600 mt-2">{query.description}</p>
                      )}
                    </div>

                    {/* Query Code */}
                    <div className="bg-gray-900 p-4 overflow-x-auto">
                      <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap">
                        {query.query}
                      </pre>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <p className="text-xs text-gray-500">
                  These queries are executed against the data warehouse to populate this page.
                  Click "Copy SQL" to copy any query to your clipboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
